import { financeNumber } from '../pendingInvoiceCost';
import api from '../../../services/api';

// Single home for the "Rachunek wyników" (income statement / EBITDA bridge)
// contract. Backend: GET /api/reports/income-statement — composed from
// reportService.getCompanyBalanceReport, same from/to (YYYY-MM) contract and
// 24-month cap as /company-balance. See docs/modules/finance-and-margin.md
// and docs/decisions/2026-09-12-rachunek-wynikow-ebitda.md.

export type IncomeStatementBasis = 'forecast' | 'actual';

export interface IncomeStatementLine {
  revenue: number;
  // `null` means an unknown cost (pending FIFO batch) — never coerced to 0.
  // Once `margin` is null, `ebitda`/`profit` and their percents are null too.
  orderCosts: number | null;
  margin: number | null;
  marginPercent: number | null;
  operatingFixedCosts: {
    total: number;
    byCategory: Record<string, number>;
  };
  ebitda: number | null;
  ebitdaPercent: number | null;
  depreciation: number;
  financialCosts: number;
  // null only when `ebitda` is null (unknown margin) — see incomeTax below.
  preTaxResult: number | null;
  // Always numeric: the entered INCOME_TAX cost for the period, 0 when none.
  incomeTaxEntered: number;
  // 9% of a positive `preTaxResult`, 0 for a non-positive one, null when
  // `preTaxResult` is unknown — see docs/decisions/2026-09-12-rachunek-wynikow-ebitda.md.
  incomeTaxEstimated: number | null;
  // 'entered' when `incomeTaxEntered > 0` overrode the estimate, 'estimated'
  // otherwise (including when both are 0/unknown).
  incomeTaxSource: 'entered' | 'estimated';
  incomeTax: number | null;
  profit: number | null;
  profitPercent: number | null;
}

export interface IncomeStatementMonth {
  month: string;
  isCurrent: boolean;
  isFuture: boolean;
  actual: IncomeStatementLine;
  forecast: IncomeStatementLine;
}

export type IncomeStatementReviewState =
  | 'INCLUDED'
  | 'PARTIAL'
  | 'READY'
  | 'REVIEW'
  | 'PLANNED'
  | 'EXCLUDED';

export interface IncomeStatementCustomerOrderMoney {
  revenue: number;
  orderCosts: number | null;
  margin: number | null;
}

export interface IncomeStatementCustomerOrder {
  id: string;
  orderNumber: string;
  displayOrderNumber: string;
  status: string;
  plannedDate: string | null;
  reviewState: IncomeStatementReviewState;
  requiresReview: boolean;
  inActual: boolean;
  inForecast: boolean;
  // DELIVERED, in forecast, with no settlement entry at all yet (ever, not
  // just this period) — see docs/decisions/2026-09-21-rachunek-wynikow-nierozliczone-i-przeglad.md.
  awaitingSettlement: boolean;
  actual: IncomeStatementCustomerOrderMoney | null;
  forecast: IncomeStatementCustomerOrderMoney | null;
}

export interface IncomeStatementAwaitingSettlementSummary {
  orderCount: number;
  revenue: number;
}

export interface IncomeStatementOutsidePlanOrder {
  id: string;
  orderNumber: string;
  displayOrderNumber: string;
  customerName: string;
  plannedDate: string | null;
  reviewReasons: string[];
  revenue: number;
}

export interface IncomeStatementOutsidePlan {
  orderCount: number;
  revenue: number;
  orders: IncomeStatementOutsidePlanOrder[];
}

export interface IncomeStatementCustomerTotals {
  orderCount: number;
  revenue: number;
  orderCosts: number | null;
  margin: number | null;
  marginPercent: number | null;
}

export interface IncomeStatementCustomer {
  key: string;
  id: string | null;
  name: string;
  taxId: string | null;
  address: string | null;
  actual: IncomeStatementCustomerTotals;
  forecast: IncomeStatementCustomerTotals;
  orders: IncomeStatementCustomerOrder[];
}

export interface IncomeStatementPurchase {
  /** INVOICE / PLAN; WP2: CORRECTION and WITHDRAWAL carry a signed difference in their own month. */
  kind: 'INVOICE' | 'PLAN' | 'CORRECTION' | 'WITHDRAWAL';
  supplierOrderId: string; orderNumber: string; supplierName: string;
  invoiceId: string | null; documentNumber: string | null; month: string; actual: boolean;
  contractCurrency: string; contractNet: number; contractNetPLN: number; contractRate: number | null;
  invoiceCurrency: string | null; invoiceNet: number | null; invoiceRate: number | null;
  netPLN: number; extraCostPLN: number;
}
// WP6 "Prognoza a wynik" (docs/decisions/2026-09-24-wp6-prognoza-a-wynik.md).
// Customer orders are on the order-margin basis (WP5 plan / ledger COGS), ZD
// purchases on the purchase basis; the two are never added together.
export interface ForecastVsActualOrderTotals {
  orderCount: number;
  revenue: number;
  // null = at least one order has an unknown cost (never coerced to 0).
  totalCost: number | null;
  margin: number | null;
  marginPercent: number | null;
  pendingCostOrderCount: number;
}

export interface ForecastVsActualPurchaseTotals {
  count: number;
  amountPLN: number;
}

export interface ForecastVsActualBasis {
  orders: ForecastVsActualOrderTotals;
  purchases: ForecastVsActualPurchaseTotals;
}

export interface ForecastVsActualMonth {
  month: string;
  forecast: ForecastVsActualBasis;
  actual: ForecastVsActualBasis;
}

export interface ForecastVsActualMoney {
  revenue: number | null;
  totalCost: number | null;
  margin: number | null;
}

export interface ForecastVsActualOrder {
  id: string;
  orderNumber: string;
  displayOrderNumber: string;
  status: string;
  customer: { id: string | null; name: string } | null;
  // Forecast: the planned month; actual: the sales invoice recognition month.
  month: string | null;
  revenue: number;
  totalCost: number | null;
  margin: number | null;
  marginPercent: number | null;
  costPending: boolean;
  // Actual only: the already realized part of a pending ledger cost.
  knownCost: number | null;
  // Actual only: unissued goods of an invoiced order priced by the WP5 plan
  // at the average frozen at the invoice — never part of the actual cost.
  unissued: { averageCostPLN: number | null; costUnknown: boolean } | null;
  // Actual only: FIFO cost of goods issued beyond the invoice (question 2.8),
  // informational — never part of the actual cost, margin or totals.
  issuedOverInvoiceCostPLN: number | null;
  // Snapshot at the first confirmation; null when never confirmed.
  planned: (ForecastVsActualMoney & { snapshotAt: string | null }) | null;
  delta: ForecastVsActualMoney | null;
}

export interface ForecastVsActual {
  months: ForecastVsActualMonth[];
  totals: { forecast: ForecastVsActualBasis; actual: ForecastVsActualBasis };
  orders: { forecast: ForecastVsActualOrder[]; actual: ForecastVsActualOrder[] };
  // Period sum of issuedOverInvoiceCostPLN over the actual orders; not in totals.
  issuedOverInvoice: { orderCount: number; costPLN: number };
}

export interface IncomeStatementResponse {
  purchases?: IncomeStatementPurchase[];
  forecastVsActual: ForecastVsActual;
  from: string;
  to: string;
  currentMonth: string;
  months: IncomeStatementMonth[];
  totals: { actual: IncomeStatementLine; forecast: IncomeStatementLine };
  customers: IncomeStatementCustomer[];
  awaitingSettlementSummary: IncomeStatementAwaitingSettlementSummary;
  outsidePlan: IncomeStatementOutsidePlan;
}

function record(value: unknown): Record<string, any> {
  return value && typeof value === 'object' ? value as Record<string, any> : {};
}

function num(value: unknown): number {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

function text(value: unknown, fallback = ''): string {
  return typeof value === 'string' ? value : fallback;
}

function bool(value: unknown): boolean {
  return value === true;
}

function nullableText(value: unknown): string | null {
  return typeof value === 'string' && value ? value : null;
}

function normalizeByCategory(value: unknown): Record<string, number> {
  const source = record(value);
  return Object.fromEntries(Object.entries(source).map(([key, amount]) => [key, num(amount)]));
}

function normalizeLine(value: unknown): IncomeStatementLine {
  const source = record(value);
  const operatingFixedCosts = record(source.operatingFixedCosts);
  return {
    revenue: num(source.revenue),
    orderCosts: financeNumber(source.orderCosts),
    margin: financeNumber(source.margin),
    marginPercent: financeNumber(source.marginPercent),
    operatingFixedCosts: {
      total: num(operatingFixedCosts.total),
      byCategory: normalizeByCategory(operatingFixedCosts.byCategory),
    },
    ebitda: financeNumber(source.ebitda),
    ebitdaPercent: financeNumber(source.ebitdaPercent),
    depreciation: num(source.depreciation),
    financialCosts: num(source.financialCosts),
    preTaxResult: financeNumber(source.preTaxResult),
    incomeTaxEntered: num(source.incomeTaxEntered),
    incomeTaxEstimated: financeNumber(source.incomeTaxEstimated),
    incomeTaxSource: source.incomeTaxSource === 'entered' ? 'entered' : 'estimated',
    incomeTax: financeNumber(source.incomeTax),
    profit: financeNumber(source.profit),
    profitPercent: financeNumber(source.profitPercent),
  };
}

function normalizeMonth(value: unknown): IncomeStatementMonth {
  const source = record(value);
  return {
    month: text(source.month),
    isCurrent: bool(source.isCurrent),
    isFuture: bool(source.isFuture),
    actual: normalizeLine(source.actual),
    forecast: normalizeLine(source.forecast),
  };
}

function normalizeCustomerTotals(value: unknown): IncomeStatementCustomerTotals {
  const source = record(value);
  return {
    orderCount: num(source.orderCount),
    revenue: num(source.revenue),
    orderCosts: financeNumber(source.orderCosts),
    margin: financeNumber(source.margin),
    marginPercent: financeNumber(source.marginPercent),
  };
}

function normalizeCustomerOrderMoney(value: unknown): IncomeStatementCustomerOrderMoney | null {
  if (!value || typeof value !== 'object') return null;
  const source = record(value);
  return {
    revenue: num(source.revenue),
    orderCosts: financeNumber(source.orderCosts),
    margin: financeNumber(source.margin),
  };
}

function normalizeCustomerOrder(value: unknown): IncomeStatementCustomerOrder {
  const source = record(value);
  return {
    id: text(source.id),
    orderNumber: text(source.orderNumber),
    displayOrderNumber: text(source.displayOrderNumber, text(source.orderNumber)),
    status: text(source.status),
    plannedDate: nullableText(source.plannedDate),
    reviewState: text(source.reviewState, 'REVIEW') as IncomeStatementReviewState,
    requiresReview: bool(source.requiresReview),
    inActual: bool(source.inActual),
    inForecast: bool(source.inForecast),
    awaitingSettlement: bool(source.awaitingSettlement),
    actual: normalizeCustomerOrderMoney(source.actual),
    forecast: normalizeCustomerOrderMoney(source.forecast),
  };
}

function normalizeAwaitingSettlementSummary(value: unknown): IncomeStatementAwaitingSettlementSummary {
  const source = record(value);
  return {
    orderCount: num(source.orderCount),
    revenue: num(source.revenue),
  };
}

function normalizeOutsidePlanOrder(value: unknown): IncomeStatementOutsidePlanOrder {
  const source = record(value);
  return {
    id: text(source.id),
    orderNumber: text(source.orderNumber),
    displayOrderNumber: text(source.displayOrderNumber, text(source.orderNumber)),
    customerName: text(source.customerName, '—'),
    plannedDate: nullableText(source.plannedDate),
    reviewReasons: Array.isArray(source.reviewReasons) ? source.reviewReasons.filter((reason: unknown) => typeof reason === 'string') : [],
    revenue: num(source.revenue),
  };
}

function normalizeOutsidePlan(value: unknown): IncomeStatementOutsidePlan {
  const source = record(value);
  return {
    orderCount: num(source.orderCount),
    revenue: num(source.revenue),
    orders: Array.isArray(source.orders) ? source.orders.map(normalizeOutsidePlanOrder) : [],
  };
}

function normalizeCustomer(value: unknown): IncomeStatementCustomer {
  const source = record(value);
  return {
    key: text(source.key, 'none'),
    id: nullableText(source.id),
    name: text(source.name, '—'),
    taxId: nullableText(source.taxId),
    address: nullableText(source.address),
    actual: normalizeCustomerTotals(source.actual),
    forecast: normalizeCustomerTotals(source.forecast),
    orders: Array.isArray(source.orders) ? source.orders.map(normalizeCustomerOrder) : [],
  };
}

function normalizeForecastOrderTotals(value: unknown): ForecastVsActualOrderTotals {
  const source = record(value);
  return {
    orderCount: num(source.orderCount),
    revenue: num(source.revenue),
    totalCost: financeNumber(source.totalCost),
    margin: financeNumber(source.margin),
    marginPercent: financeNumber(source.marginPercent),
    pendingCostOrderCount: num(source.pendingCostOrderCount),
  };
}

function normalizeForecastBasis(value: unknown): ForecastVsActualBasis {
  const source = record(value);
  const purchases = record(source.purchases);
  return {
    orders: normalizeForecastOrderTotals(source.orders),
    purchases: { count: num(purchases.count), amountPLN: num(purchases.amountPLN) },
  };
}

function normalizeForecastMoney(value: unknown): ForecastVsActualMoney | null {
  if (!value || typeof value !== 'object') return null;
  const source = record(value);
  return {
    revenue: financeNumber(source.revenue),
    totalCost: financeNumber(source.totalCost),
    margin: financeNumber(source.margin),
  };
}

function normalizeForecastOrder(value: unknown): ForecastVsActualOrder {
  const source = record(value);
  const customer = source.customer && typeof source.customer === 'object' ? record(source.customer) : null;
  const unissued = source.unissued && typeof source.unissued === 'object' ? record(source.unissued) : null;
  const planned = normalizeForecastMoney(source.planned);
  return {
    id: text(source.id),
    orderNumber: text(source.orderNumber),
    displayOrderNumber: text(source.displayOrderNumber, text(source.orderNumber)),
    status: text(source.status),
    customer: customer ? { id: nullableText(customer.id), name: text(customer.name, '—') } : null,
    month: nullableText(source.month),
    revenue: num(source.revenue),
    totalCost: financeNumber(source.totalCost),
    margin: financeNumber(source.margin),
    marginPercent: financeNumber(source.marginPercent),
    costPending: bool(source.costPending),
    knownCost: financeNumber(source.knownCost),
    unissued: unissued
      ? { averageCostPLN: financeNumber(unissued.averageCostPLN), costUnknown: bool(unissued.costUnknown) }
      : null,
    issuedOverInvoiceCostPLN: financeNumber(source.issuedOverInvoiceCostPLN),
    planned: planned ? { ...planned, snapshotAt: nullableText(record(source.planned).snapshotAt) } : null,
    delta: normalizeForecastMoney(source.delta),
  };
}

export function normalizeForecastVsActual(value: unknown): ForecastVsActual {
  const source = record(value);
  const totals = record(source.totals);
  const orders = record(source.orders);
  return {
    months: Array.isArray(source.months) ? source.months.map((entry: unknown) => {
      const month = record(entry);
      return {
        month: text(month.month),
        forecast: normalizeForecastBasis(month.forecast),
        actual: normalizeForecastBasis(month.actual),
      };
    }) : [],
    totals: { forecast: normalizeForecastBasis(totals.forecast), actual: normalizeForecastBasis(totals.actual) },
    issuedOverInvoice: {
      orderCount: num(record(source.issuedOverInvoice).orderCount),
      costPLN: num(record(source.issuedOverInvoice).costPLN),
    },
    orders: {
      forecast: Array.isArray(orders.forecast) ? orders.forecast.map(normalizeForecastOrder) : [],
      actual: Array.isArray(orders.actual) ? orders.actual.map(normalizeForecastOrder) : [],
    },
  };
}

export function normalizeIncomeStatement(value: unknown): IncomeStatementResponse {
  const source = record(value);
  const totals = record(source.totals);
  return {
    purchases: Array.isArray(source.purchases) ? source.purchases.map((value: unknown) => {
      const row = record(value);
      const kind = ['PLAN', 'CORRECTION', 'WITHDRAWAL'].includes(text(row.kind)) ? text(row.kind) as IncomeStatementPurchase['kind']
        : bool(row.actual) ? 'INVOICE' : 'PLAN';
      return { kind, supplierOrderId: text(row.supplierOrderId), orderNumber: text(row.orderNumber), supplierName: text(row.supplierName),
        invoiceId: nullableText(row.invoiceId), documentNumber: nullableText(row.documentNumber), month: text(row.month), actual: bool(row.actual),
        contractCurrency: text(row.contractCurrency), contractNet: num(row.contractNet), contractNetPLN: num(row.contractNetPLN), contractRate: financeNumber(row.contractRate),
        invoiceCurrency: nullableText(row.invoiceCurrency), invoiceNet: financeNumber(row.invoiceNet), invoiceRate: financeNumber(row.invoiceRate),
        netPLN: num(row.netPLN), extraCostPLN: num(row.extraCostPLN) };
    }) : [],
    from: text(source.from),
    to: text(source.to),
    currentMonth: text(source.currentMonth),
    months: Array.isArray(source.months) ? source.months.map(normalizeMonth) : [],
    totals: {
      actual: normalizeLine(totals.actual),
      forecast: normalizeLine(totals.forecast),
    },
    customers: Array.isArray(source.customers) ? source.customers.map(normalizeCustomer) : [],
    awaitingSettlementSummary: normalizeAwaitingSettlementSummary(source.awaitingSettlementSummary),
    outsidePlan: normalizeOutsidePlan(source.outsidePlan),
    forecastVsActual: normalizeForecastVsActual(source.forecastVsActual),
  };
}

export async function fetchIncomeStatement(from: string, to: string): Promise<IncomeStatementResponse> {
  const response = await api.get('/reports/income-statement', { params: { from, to } });
  return normalizeIncomeStatement(response.data);
}
